import React, { useEffect } from 'react';
import { TextField, Box, Typography, Alert } from '@mui/material';

interface Props {
  data: any;
  onChange: (data: any) => void;
  onComplete: (completed: boolean) => void;
}

const NoveltyCommercial: React.FC<Props> = ({ data, onChange, onComplete }) => {
  const handleChange = (field: string, value: any) => {
    const newData = { ...data, [field]: value };
    onChange(newData);
    const isComplete = !!(
      newData.innovativeFeatures?.length >= 75 &&
      newData.keyAdvantages?.length >= 50 &&
      newData.realWorldApplications?.length >= 50
    );
    onComplete(isComplete);
  };

  useEffect(() => {
    const isComplete = !!(
      data.innovativeFeatures?.length >= 75 &&
      data.keyAdvantages?.length >= 50 &&
      data.realWorldApplications?.length >= 50
    );
    onComplete(isComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.innovativeFeatures, data.keyAdvantages, data.realWorldApplications]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Novelty & Commercial Value</Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          What major gap has the work filled with respect to existing solutions? Each novel feature should be noted in separate paragraphs.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Guidelines for Patent-Ready Description:
        </Typography>

        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          <strong>Innovative Features:</strong> Describe what is new in technical terms. Focus on the "how" not the "what benefit." For example: "Uses a dual-layer filtration membrane with variable pore sizes" rather than "filters water better." Each novel aspect should be a separate paragraph. This forms the basis of your patent claims.
        </Typography>

        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          <strong>Key Advantages:</strong> Now describe the benefits and improvements. Be specific and quantifiable: "Reduces processing time by 40%" or "Operates at 30% lower temperature." Compare directly to existing solutions.
        </Typography>

        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          <strong>Real-world Applications:</strong> Identify specific industries, use cases, and target markets. Include potential market size if known. Describe who will use it and how: "Manufacturing facilities processing 1000+ units/day" rather than just "manufacturers."
        </Typography>

        <Typography variant="body2">
          <strong>Potential Partners:</strong> List specific companies, industries, or organization types that would license or commercialize this technology.
        </Typography>
      </Alert>

      <TextField
        fullWidth
        required
        multiline
        rows={5}
        label="Innovative Features"
        value={data.innovativeFeatures || ''}
        onChange={(e) => handleChange('innovativeFeatures', e.target.value)}
        margin="normal"
        error={data.innovativeFeatures && data.innovativeFeatures.length < 75}
        helperText={
          data.innovativeFeatures && data.innovativeFeatures.length < 75
            ? `Must be at least 75 characters (currently ${data.innovativeFeatures.length})`
            : "What's new and different about your invention? Describe each innovative feature in separate paragraphs. (min. 75 characters)"
        }
        inputProps={{ maxLength: 2000 }}
      />

      <TextField
        fullWidth
        required
        multiline
        rows={4}
        label="Key Advantages"
        value={data.keyAdvantages || ''}
        onChange={(e) => handleChange('keyAdvantages', e.target.value)}
        margin="normal"
        error={data.keyAdvantages && data.keyAdvantages.length < 50}
        helperText={
          data.keyAdvantages && data.keyAdvantages.length < 50
            ? `Must be at least 50 characters (currently ${data.keyAdvantages.length})`
            : "What advantages does your invention offer over existing solutions? (min. 50 characters)"
        }
        inputProps={{ maxLength: 1500 }}
      />

      <TextField
        fullWidth
        required
        multiline
        rows={4}
        label="Real-world Applications"
        value={data.realWorldApplications || ''}
        onChange={(e) => handleChange('realWorldApplications', e.target.value)}
        margin="normal"
        error={data.realWorldApplications && data.realWorldApplications.length < 50}
        helperText={
          data.realWorldApplications && data.realWorldApplications.length < 50
            ? `Must be at least 50 characters (currently ${data.realWorldApplications.length})`
            : "Where can this be used? Describe target users/market, potential market size, and impact. (min. 50 characters)"
        }
        inputProps={{ maxLength: 1500 }}
      />

      <TextField
        fullWidth
        multiline
        rows={3}
        label="Potential Partners or Licensees"
        value={data.potentialPartners || ''}
        onChange={(e) => handleChange('potentialPartners', e.target.value)}
        margin="normal"
        helperText="Companies, organizations, or industries that might be interested in licensing or partnering (optional)"
        inputProps={{ maxLength: 1000 }}
      />
    </Box>
  );
};

export default NoveltyCommercial;
